// apps/web/src/env.ts
// Typed client-side environment loader. Use this in web code to get strongly-typed VITE_* values.
import { parseWebEnv } from '@leangap/types/schemas/env'

// Convert import.meta.env into a plain map for validation
const buildEnv: Record<string, string | undefined> = {}
for (const k of Object.keys(import.meta.env)) {
	const v = (import.meta.env as Record<string, unknown>)[k]
	buildEnv[k] = typeof v === 'string' ? v : undefined
}

// Internal holder for merged env (populated by initEnv). Start with build-time defaults.
let mergedEnv: Record<string, string | undefined> = { ...buildEnv }
let parsedEnv = parseWebEnv(mergedEnv)

// initEnv now reads inline-injected window.__APP_ENV__ (populated at deploy-time
// in index.html by the start script). This avoids an extra network request and
// prevents accidental exposure of *all* VITE_* vars by using an allowlist.
export async function initEnv(): Promise<void> {
	if (typeof window === 'undefined') return
	try {
		const runtime = (window as any).__APP_ENV__ as Record<string, string | undefined> | undefined
		if (runtime && typeof runtime === 'object') {
			mergedEnv = { ...buildEnv, ...runtime }
			parsedEnv = parseWebEnv(mergedEnv)
		} else {
			// eslint-disable-next-line no-console
			console.warn('window.__APP_ENV__ not found; using build-time env only')
		}
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn('Failed to read inline runtime env; using build-time env', e)
	}
}

export function getWebEnv() {
	return parsedEnv
}

/**
 * Read a string env value from the merged env.
 * Usage: envString('VITE_HUBSPOT_REDIRECT_URL')
 */
export function envString(name: string): string | undefined {
	return (getWebEnv() as Record<string, string | undefined>)[name]
}

/**
 * Read a boolean-like env value. Returns true for '1','true','yes','on' (case-insensitive).
 */
export function envBool(name: string): boolean {
	const v = envString(name)
	return ['1', 'true', 'yes', 'on'].includes((v ?? '').toLowerCase())
}

export const VITE_API_URL = () => getWebEnv().VITE_API_URL
export const VITE_PUBLIC_APP_URL = () => getWebEnv().VITE_PUBLIC_APP_URL
export const VITE_RECAPTCHA_V3_KEY = () => getWebEnv().VITE_RECAPTCHA_V3_KEY
export const VITE_HUBSPOT_CLIENT_TOKEN = () => getWebEnv().VITE_HUBSPOT_CLIENT_TOKEN
export const VITE_APP_ENV = () => getWebEnv().VITE_APP_ENV ?? (import.meta.env.MODE as string)
export const VITE_GTM_ID = () =>
	(getWebEnv() as unknown as Record<string, string | undefined>).VITE_GTM_ID

// Build-time or runtime Vite variables (accessors)
export const VITE_SENTRY_DSN = () =>
	(getWebEnv() as Record<string, string | undefined>)['VITE_SENTRY_DSN']
export const VITE_SENTRY_ENV = () =>
	(getWebEnv() as Record<string, string | undefined>)['VITE_SENTRY_ENV']
export const VITE_SENTRY_RELEASE = () =>
	(getWebEnv() as Record<string, string | undefined>)['VITE_SENTRY_RELEASE']
export const VITE_SENTRY_TRACES_SAMPLE_RATE = () =>
	(getWebEnv() as Record<string, string | undefined>)['VITE_SENTRY_TRACES_SAMPLE_RATE']

// HubSpot build-time or runtime defaults (declared in schema)
export const VITE_HUBSPOT_REDIRECT_ENABLED = () =>
	(getWebEnv() as Record<string, string | undefined>)['VITE_HUBSPOT_REDIRECT_ENABLED']
export const VITE_HUBSPOT_REDIRECT_URL = () =>
	(getWebEnv() as Record<string, string | undefined>)['VITE_HUBSPOT_REDIRECT_URL']

export default getWebEnv
